<template>
  <div>
    <div
      v-if="canAccessAmr"
      class="wrapper"
      :style="{ '--uncollapsed-width': uncollapsedWidth }"
    >
      <div>
        <nav-bar>
          <template v-slot:languageSwitcher>
            <select v-model="language" @change="onLanguageSelected">
              <option :value="'en-US'">en-US</option>
              <option :value="'en-GB'">en-GB</option>
              <option :value="'fr-FR'">fr-FR</option>
            </select>
          </template>
          <template v-slot:searchBar> <searchBar></searchBar> </template>
        </nav-bar>
      </div>
      <div class="flex-page-container">
        <div class="side-menu-container">
          <SideBarMenu
            :menu="menu"
            @item-click="onItemClick"
            @update:collapsed="trackMenuState"
            :collapsed="true"
            :theme="'suez-theme'"
            :width="uncollapsedWidth"
            :relative="true"
          >
            <template v-slot:toggle-icon>
              <span
                :class="menuColapsed ? 'scf-arrow-right' : 'scf-arrow-left'"
              ></span>
            </template>
          </SideBarMenu>
        </div>
        <div class="content-container">
          <router-view />
        </div>
      </div>
      <LoadingSpinner :loading="isLoading" />
      <HttpErrorMessage
        :displayLang="$i18n.locale"
        :httpCode="errorCode"
        :show="showErrorMessage"
        :hostElement="getPopupHost()"
        v-on:msg-hidden="this.showErrorMessage = false"
      />
    </div>
    <div v-else class="wrapper">
      <router-view />
    </div>
  </div>
</template>

<script>
import { computed, defineComponent } from "vue";
import { useStore } from "vuex";

import * as wjcCore from "@grapecity/wijmo";

import ModulesLoader from "@/_services/ModulesLoader.js";
import MasterCommunicationHandler from "@/_services/MasterCommunicationHandler.js";
import MenuHelper from "@/_helpers/MenuHelper.js";
import ErrorsHelper from "@/_helpers/ErrorsHelper.js";
import LanguageHelper from "@/_helpers/LanguageHelper.js";

import SideBarMenu from "@supervision/vue-components/components/Layout/SideBar/SidebarMenu.vue";
import LoadingSpinner from "@supervision/vue-components/components/LoadingSpinner.vue";
import NavBar from "./components/NavBar.vue";
import searchBar from "@/components/atoms/SearchBar.vue";
import HttpErrorMessage from "@supervision/vue-components/components/HttpErrorMessage.vue";

import Environment from "@/../config";

export default defineComponent({
  name: "AmrPortal",
  components: {
    LoadingSpinner,
    NavBar,
    searchBar,
    SideBarMenu,
    HttpErrorMessage,
  },
  provide() {
    return {
      renderEntryDiv: computed(() => this.renderEntryDiv),
      rootElements: computed(() => this.rootElements),
      lang: computed(() => this.language),
      loggedInUser: computed(() => this.userInfo),
      accessToken: computed(() => this.keycloak.token),
      appsLoaded: computed(() => this.appsLoaded),
    };
  },
  setup() {
    const store = useStore();
    const keycloak = computed(() => store.state.keycloakInstance);
    return {
      keycloak,
    };
  },
  data() {
    const self = this;
    return {
      errorCode: 500,
      showErrorMessage: false,
      config: Environment,
      appsLoaded: false,
      userInfo: null,
      canAccessAmr:
        this.keycloak.tokenParsed.role.includes("AmrPortal-access") ||
        this.keycloak.hasRealmRole("AmrPortal-access"),
      language: this.$i18n.locale,
      rootElements: [],
      renderEntryDiv: true,
      isLoading: true,
      modulesLoader: new ModulesLoader(self),
      masterHandler: new MasterCommunicationHandler(self),
      sideMenuHelper: MenuHelper,
      uncollapsedWidth: "280px",
      menuColapsed: true,
      modules: [],
      treatModule: null,
      menu: [],
      initialMenuCount: 0,
      latestRoute: "",
      auxMenu: [
        {
          href: "/" + this.$realm + "/home",
          tcode: "amrHome",
          exact: false,
          external: false,
          icon: {
            element: "i",
            class: "scf-patrimoine",
            attributes: { style: "font-size: xx-large;" },
          },
          category: {
            code: "home",
            priority: 1,
          },
        },
      ],
    };
  },
  methods: {
    onLanguageSelected(event) {
      LanguageHelper.saveLocale(
        this.keycloak.tokenParsed?.sub,
        event.target.value
      );
    },
    getPopupHost() {
      // TODO : remove this code once migration is over
      let oldSupervisionLayoutHost = document.getElementById("#layoutContent");
      if (oldSupervisionLayoutHost && oldSupervisionLayoutHost !== null)
        return oldSupervisionLayoutHost;
      return null;
    },
    showErrorDialog(code) {
      this.errorCode = parseInt(code);
      this.showErrorMessage = true;
    },
    trackMenuState(colapsed) {
      this.menuColapsed = colapsed;
    },
    onItemClick(event, item) {
      event.preventDefault();
      if (!item.href || item.href.length == 0) return;
      if (item.href == "/" + this.$realm + "/home") {
        this.$router.push({ name: "amrHome" });
      } else {
        if (item.href == this.$route.href) this.$router.go(-1);
        else this.$router.push({ path: item.href });
      }
      this.updateActiveRoute(item.href);
    },
    updateActiveRoute(href) {
      [...document.getElementsByClassName("vsm--link_active")].forEach((el) => {
        el.classList.remove("vsm--link_active");
      });
      let linkElement = document.querySelector(`[href="${href}"]`);
      if (linkElement !== null) linkElement.classList.add("vsm--link_active");
    },
    arrangeMenu() {
      let uncategorizedItems = [];
      this.auxMenu.forEach((menuItem) => {
        if (this.sideMenuHelper.ItemHasCategory(menuItem)) {
          this.sideMenuHelper.PushToCategory(
            menuItem,
            computed(() =>
              this.$t(`sideBar.categories.${menuItem.category.code}`)
            )
          );
        } else {
          uncategorizedItems.push(menuItem);
        }
      });
      const arrangedMenu =
        this.sideMenuHelper.GetArrangedMenu(uncategorizedItems);
      this.sideMenuHelper.TranslateMenu(this);
      return arrangedMenu.filter(
        (am) => am.href !== "" || (am.child && am.child.length > 0)
      );
    },
    hasAnyAppendedMenu() {
      return this.initialMenuCount < this.auxMenu.length;
    },
    cleanUp() {
      this.masterHandler.Destroy();
      delete this.auxMenu;
      delete this.treatModule;
      if (!this.pageAccessedByReload()) {
        this.latestRoute = "/" + this.$realm + "/home";
      }
      if (this.$route.name == "amrHome" && this.latestRoute.endsWith("home")) {
        this.isLoading = false;
        this.appsLoaded = true;
        return;
      }
      this.$router.push({ path: this.latestRoute });
      this.isLoading = false;
      this.appsLoaded = true;
    },
    finishUp() {
      this.menu = this.arrangeMenu();
      this.cleanUp();
    },
    loadModules() {
      if (!this.canAccessAmr) return;
      this.modulesLoader
        .GetAppsLibraries()
        .then(async (modules) => {
          modules = modules
            .filter(
              (el) =>
                el.isIgnored != undefined &&
                el.isIgnored.toLowerCase() == "false"
            )
            .map((el) => {
              el.isIgnored = false;
              return el;
            });
          const safeModules = [];
          for (const [index, module] of modules.entries()) {
            try {
              module.base = module.base.endsWith("/")
                ? module.base
                : module.base + "/";
              if (
                this.keycloak.tokenParsed.role.includes(module.requiredRole) ||
                this.keycloak.hasRealmRole(module.requiredRole)
              ) {
                this.modulesLoader.InjectBuildInfo(modules, index);
                this.modulesLoader.InjectRootElement(module.moduleName);
                safeModules.push(module);
              } else {
                await this.keycloak.loadUserInfo().then((userInfo) => {
                  ErrorsHelper.UserCannotAccessModule(
                    module.moduleName,
                    userInfo.name
                  );
                });
                this.modulesLoader.InjectBuildInfo(modules, index);
                module.isIgnored = true;
              }
            } catch (err) {
              // Module failed to load, mark as ignored and continue
              module.isIgnored = true;
              console.error(`Module ${module.moduleName} failed to load:`, err);
            }
          }
          return safeModules;
        })
        .then((modules) => {
          this.modules = modules.sort((a, b) => a.priority - b.priority);
        });
    },
    pageAccessedByReload() {
      return (
        (window.performance.navigation &&
          window.performance.navigation.type === 1) ||
        window.performance
          .getEntriesByType("navigation")
          .map((nav) => nav.type)
          .includes("reload")
      );
    },
    showErrorPage(code = 499) {
      this.$router.replace({
        name: "error",
        params: { code: code },
      });
    },
    interceptFetch() {
      // Intercepting fetch/XMLHttpRequest requests globally
      const originalSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.send = function () {
        const response = this;
        // Hook into the onload event to get the response
        this.addEventListener("load", function () {
          if (!(response.status >= 200 && response.status < 300)) {
            if ([404, 500, 401, 403].includes(response.status))
              self.showErrorDialog(response.status);
            else self.showErrorDialog("unknown");
          }
        });
        originalSend.apply(this, arguments);
      };

      const originalFetch = window.fetch;
      const self = this;
      window.fetch = function (...args) {
        return originalFetch
          .apply(this, args)
          .then((response) => {
            if (!response.ok) {
              if ([404, 500, 401, 403].includes(response.status))
                self.showErrorDialog(response.status);
              else self.showErrorDialog("unknown");

              response.json = () => ({});
              return response;
            }

            return response;
          })
          .catch((error) => {
            const response = {
              data: {},
              error: error,
              status: 500,
              ok: false,
              json: () => ({}),
            };
            self.showErrorDialog(500);
            return response;
          });
      };
    },
  },
  watch: {
    auxMenu() {
      if (this.treatModule === -1) {
        this.finishUp();
      }
    },
    treatModule() {
      if (
        this.modules[this.treatModule] &&
        this.modules[this.treatModule].isIgnored
      ) {
        this.treatModule++;
        return;
      }
      if (this.modules.length == 0) return;
      if (this.treatModule === -1) {
        if (
          !this.hasAnyAppendedMenu() ||
          this.modules[this.modules.length - 1].isIgnored
        ) {
          this.finishUp();
        }
        return;
      }
      const self = this;
      const module = this.modules[this.treatModule];

      this.masterHandler.Register(module, () => {
        self.renderEntryDiv = false;
        if (self.modules[self.treatModule + 1]) {
          self.treatModule++;
        } else {
          self.treatModule = -1;
        }
      });
      this.modulesLoader.AddLibrariesToDOM(module);
    },
    language(newVal) {
      this.$i18n.locale = newVal;
      this.sideMenuHelper.TranslateMenu(this);
    },
  },
  mounted() {
    this.latestRoute = localStorage.getItem("latest_visitied");
    if (this.$route.name !== "amrHome") this.$router.push({ name: "loading" });
    this.loadModules();
  },
  created() {
    this.interceptFetch();
    wjcCore.setLicenseKey(this.config.value("VUE_APP_WIJMO_LICENCE"));
    const userInfo = this.$jwt.userProfile;
    userInfo.sub = this.$jwt.parsedToken["sub"];
    this.userInfo = userInfo;

    const initialLocale = LanguageHelper.getInitialLocale(
      this.keycloak.tokenParsed,
      this.$i18n.availableLocales
    );
    this.language = initialLocale;
    this.$i18n.locale = initialLocale;

    // Uncomment to activate AmrPortal block: this will not load apps when no scopes for user
    // if (!this.$jwt.scope || this.$jwt.scope.length == 0) {
    //   this.$router.push({
    //     name: "access_forbidden",
    //     params: { code: 499 },
    //   });
    //   this.isLoading = false;
    // }
  },
});
</script>

<style lang="css">
@import url("@suez/custom-icons/suez-glyphicons.css");
body {
  overflow: hidden;
  font-family: D-DIN !important;
}
.wrapper,
html,
body {
  height: 100vh;
  margin: 0;
}
.wrapper {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.flex-page-container {
  flex: 2;
  display: flex;
  overflow: hidden;
}
.side-menu-container {
  flex: 0;
}
.content-container {
  padding: 10px;
  flex: 1 1;
  overflow-y: auto;
  overflow-x: hidden;
}
.vsm--link_mobile > .vsm--title > .vsm--arrow {
  position: absolute;
  left: calc(var(--uncollapsed-width) - 100px);
}
.vsm--mobile-bg {
  width: var(--uncollapsed-width) !important;
}
.vsm--child_mobile {
  width: calc(var(--uncollapsed-width) - 65px) !important;
}
</style>
